import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { ApiResponse } from '../shared/types';

// Type definitions
export type AttendanceStatus = 'present' | 'absent' | 'late' | 'leave' | 'working';
export type WorkStatus = 'checked_in' | 'checked_out' | 'on_break';
export type AttendanceAction = 'checkin' | 'checkout' | 'break_start' | 'break_end';

export interface WorkHours {
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

export interface BreakHours {
  hours?: number;
  minutes?: number;
  seconds: number;
}

export interface AttendanceSessionData {
  session_id: string;
  // New API structure (period attendance)
  check_in?: string;
  check_out?: string | null;
  work_hours?: string;
  // Legacy API structure (today's attendance)
  session_check_in?: string;
  session_check_out?: string | null;
  session_work_hours?: string;
  // Additional fields from today's attendance API
  session_check_in_local?: string;
  session_check_out_local?: string | null;
}

export interface AttendanceRecord {
  id: string;
  employee_id: string;
  date: string;
  shift_id?: string | null;
  check_in: string | null;
  check_out: string | null;
  // Additional fields from today's attendance API
  check_in_local?: string;
  check_out_local?: string;
  work_hours: WorkHours | string | null;
  status: AttendanceStatus;
  created_at?: string;
  updated_at?: string;
  work_status: WorkStatus;
  break_hours: BreakHours;
  break_start?: string | null;
  break_end?: string | null;
  sessions?: AttendanceSessionData[];
}

export interface AttendanceSession {
  id: string;
  attendance_id: string;
  employee_id: string;
  check_in: string | null;
  check_out: string | null;
  session_date: string;
  created_at: string;
  updated_at: string;
  work_hours: WorkHours;
}

export interface AttendanceResponse {
  attendance: AttendanceRecord;
  session: AttendanceSession;
}

export interface ClockInRequest {
  action: AttendanceAction;
  timezone?: string;
}

/**
 * Manages user attendance operations and local attendance state for the application.
 * Provides methods to perform clock-in/clock-out and break actions, fetch attendance records, and exposes reactive state for other parts of the app to consume.
 * @service
 * @providedIn root
 * @example
 *   // Inject the service in a component or another service
 *   constructor(private logger: LoggerService, private attendanceService: AttendanceService) {}
 *   // Subscribe to current attendance and check-in state
 *   this.attendanceService.currentAttendance$.subscribe(record => { ... });
 *   this.attendanceService.isCheckedIn$.subscribe(isChecked => { ... });
 * @input none - This service does not use Angular @Input bindings; it is operated via its public methods and injected where needed.
 * @output Observable<AttendanceRecord | null> currentAttendance$ - Read-only observable that emits the current/latest attendance record held in local state.
 * @output Observable<boolean> isCheckedIn$ - Read-only observable that emits whether the current user is checked in.
 * @method clockIn(): Observable<ApiResponse<AttendanceResponse>> - Performs a check-in action against the API, updates local state and check-in flag on success, and logs errors on failure.
 * @method clockOut(): Observable<ApiResponse<AttendanceResponse>> - Performs a check-out action against the API, updates local state and check-in flag on success, and logs errors on failure.
 * @method startBreak(): Observable<ApiResponse<AttendanceResponse>> - Starts a break for the current user via the API and updates local state on success.
 * @method endBreak(): Observable<ApiResponse<AttendanceResponse>> - Ends a break for the current user via the API and updates local state on success.
 * @method getCurrentAttendance(): AttendanceRecord | null - Returns the current attendance record synchronously from the internal BehaviorSubject.
 * @method isCurrentlyCheckedIn(): boolean - Returns the current check-in boolean synchronously from the internal BehaviorSubject.
 * @method getCurrentAttendanceStatus(): Observable<ApiResponse<AttendanceRecord[]>> - Fetches current attendance status from the backend, updates local state with the latest record, and logs errors.
 * @method getTodaysAttendance(): Observable<ApiResponse<AttendanceRecord>> - Retrieves today's attendance record for the logged-in user.
 * @method getAttendanceHistory(startDate: string, endDate: string): Observable<ApiResponse<AttendanceRecord[]>> - Fetches attendance history for the specified date range (includes timezone).
 * @method getAttendanceForPeriod(startDate: string, endDate: string): Observable<ApiResponse<AttendanceRecord[]>> - Retrieves attendance records for a specific period and handles/logs errors.
 * @method getEmployeeAttendanceRecords(employeeId: string, startDate: string, endDate: string): Observable<ApiResponse<AttendanceRecord[]>> - Fetches attendance records for a given employee over a date range and logs errors on failure.
 * @method updateAttendanceStatus(attendance: AttendanceRecord): void - Updates local BehaviorSubjects with a provided attendance record (used for external updates).
 * @method clearAttendanceData(): void - Clears cached attendance data and resets check-in state to false.
 * @notes
 *   - Internal helpers: performAttendanceAction(action) centralizes API calls for actions (checkin, checkout, break_start, break_end) and updates local state.
 *   - State management: uses BehaviorSubject for currentAttendance and isCheckedIn to provide both synchronous accessors and reactive streams.
 *   - Error handling: catches API errors, logs them with LoggerService, and rethrows to callers as observables errors.
 *   - Timezone handling: uses getUserTimezone() to include user timezone in relevant requests, falling back to 'UTC' and logging a warning if resolution fails.
 *   - Lifecycle: as a singleton injectable service (providedIn: 'root'), it has no Angular component lifecycle hooks; it initializes with null attendance and checked-in = false.
 */
@Injectable({
  providedIn: 'root'
})
export class AttendanceService {
  private readonly currentAttendanceSubject = new BehaviorSubject<AttendanceRecord | null>(null);
  public readonly currentAttendance$ = this.currentAttendanceSubject.asObservable();

  private readonly isCheckedInSubject = new BehaviorSubject<boolean>(false);
  public readonly isCheckedIn$ = this.isCheckedInSubject.asObservable();

  constructor(
    private readonly apiService: ApiService,
    private readonly logger: LoggerService
  ) {}

  /**
   * Clock in for the current user
   * @returns Observable with attendance response
   */
  clockIn(): Observable<ApiResponse<AttendanceResponse>> {
    return this.performAttendanceAction('checkin');
  }

  /**
   * Clock out for the current user
   * @returns Observable with attendance response
   */
  clockOut(): Observable<ApiResponse<AttendanceResponse>> {
    return this.performAttendanceAction('checkout');
  }

  /**
   * Start break for the current user
   * @returns Observable with attendance response
   */
  startBreak(): Observable<ApiResponse<AttendanceResponse>> {
    return this.performAttendanceAction('break_start');
  }

  /**
   * End break for the current user
   * @returns Observable with attendance response
   */
  endBreak(): Observable<ApiResponse<AttendanceResponse>> {
    return this.performAttendanceAction('break_end');
  }

  /**
   * Get current attendance record from local state
   * @returns Current attendance record or null
   */
  getCurrentAttendance(): AttendanceRecord | null {
    return this.currentAttendanceSubject.value;
  }

  /**
   * Check if user is currently checked in
   * @returns True if checked in, false otherwise
   */
  isCurrentlyCheckedIn(): boolean {
    return this.isCheckedInSubject.value;
  }

  /**
   * Get current attendance status for the logged-in user
   * @returns Observable with current attendance records
   */
  getCurrentAttendanceStatus(): Observable<ApiResponse<AttendanceRecord[]>> {
    return this.apiService.get<AttendanceRecord[]>('attendant/attendance').pipe(
      tap((response) => {
        if (response.status && response.data && response.data.length > 0) {
          const [latestAttendance] = response.data;
          this.updateLocalState(latestAttendance);
        }
      }),
      catchError(error => {
        this.logger.error('Error fetching current attendance status', error, 'AttendanceService');
        return throwError(() => error);
      })
    );
  }

  /**
   * Get today's attendance record for the logged-in user
   * @returns Observable with today's attendance record
   */
  getTodaysAttendance(): Observable<ApiResponse<AttendanceRecord>> {
    const [today] = new Date().toISOString().split('T');
    return this.apiService.get<AttendanceRecord>(`attendant/attendance/today?date=${today}`);
  }

  /**
   * Get attendance history for a date range
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable with attendance records for the period
   */
  getAttendanceHistory(startDate: string, endDate: string): Observable<ApiResponse<AttendanceRecord[]>> {
    const timezone = this.getUserTimezone();
    return this.apiService.get<AttendanceRecord[]>(
      `attendant/attendance/history`,
      { start_date: startDate, end_date: endDate, timezone }
    );
  }

  /**
   * Get attendance records for a specific period
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable with attendance records for the period
   */
  getAttendanceForPeriod(startDate: string, endDate: string): Observable<ApiResponse<AttendanceRecord[]>> {
    return this.apiService.get<AttendanceRecord[]>(
      `attendant/attendance-for-period`,
      { start_date: startDate, end_date: endDate }
    ).pipe(
      catchError(error => {
        this.logger.error('Error fetching attendance for period', error, 'AttendanceService');
        return throwError(() => error);
      })
    );
  }

  /**
   * Get attendance records for a specific employee
   * @param employeeId - Employee ID
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Observable with attendance records for the employee
   */
  getEmployeeAttendanceRecords(
    employeeId: string,
    startDate: string,
    endDate: string,
    timezone?: string
  ): Observable<ApiResponse<AttendanceRecord[]>> {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    };
    const tz = timezone?.trim();
    if (tz) {
      params['timezone'] = tz;
    }
    return this.apiService.get<AttendanceRecord[]>(
      `attendant/${employeeId}/attendance`,
      params
    ).pipe(
      catchError(error => {
        this.logger.error('Error fetching employee attendance records', error, 'AttendanceService');
        return throwError(() => error);
      })
    );
  }

  /**
   * Update attendance status in local state
   * @param attendance - Attendance record to update
   */
  updateAttendanceStatus(attendance: AttendanceRecord): void {
    this.updateLocalState(attendance);
  }

  /**
   * Clear all attendance data from local state
   */
  clearAttendanceData(): void {
    this.currentAttendanceSubject.next(null);
    this.isCheckedInSubject.next(false);
  }

  // Private helper methods
  /**
  * Performs an attendance action (checkin or checkout) by sending a request to the API and updating local state.
  * @example
  * performAttendanceAction('checkin')
  * Observable<ApiResponse<AttendanceResponse>>
  * @param {AttendanceAction} action - Action to perform, typically 'checkin' or 'checkout'.
  * @returns {Observable<ApiResponse<AttendanceResponse>>} Observable that emits the API response containing updated attendance data.
  **/
  private performAttendanceAction(action: AttendanceAction): Observable<ApiResponse<AttendanceResponse>> {
    const payload: ClockInRequest = { 
      action
    };
    
    return this.apiService.post<AttendanceResponse>('attendant/attendance', payload).pipe(
      tap((response) => {
        if (response.status && response.data) {
          this.updateLocalState(response.data.attendance);
          
          // Update check-in status based on action
          if (action === 'checkin') {
            this.isCheckedInSubject.next(true);
          } else if (action === 'checkout') {
            this.isCheckedInSubject.next(false);
          }
        }
      }),
      catchError(error => {
        this.logger.error(`Error performing ${action}`, error, 'AttendanceService');
        return throwError(() => error);
      })
    );
  }

  private updateLocalState(attendance: AttendanceRecord): void {
    this.currentAttendanceSubject.next(attendance);
    this.isCheckedInSubject.next(attendance.work_status === 'checked_in');
  }

  /**
   * Get user's timezone
   * @returns Timezone string (e.g., 'America/New_York') or 'UTC' as fallback
   */
  private getUserTimezone(): string {
    try {
      if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      }
    } catch (error) {
      this.logger.warn('Unable to resolve timezone, defaulting to UTC', error, 'AttendanceService');
    }
    return 'UTC';
  }
}